import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import KeycloakService from '../services/KeycloakService';

/**
 * ProtectedRoute component to handle role-based access control
 * @param {Object} props Component props
 * @param {Array} props.roles Array of required roles (optional)
 * @param {Boolean} props.requireAllRoles Whether all roles are required (default: false)
 * @param {String} props.redirectTo Redirect path if not authenticated or authorized (default: /login)
 * @param {React.ReactNode} props.children The children components to render when authorized
 */
const ProtectedRoute = ({
  children,
  roles = [],
  requireAllRoles = false,
  redirectTo = '/login',
}) => {
  const { authenticated, loading, hasAnyRole, hasAllRoles } = useAuth();
  const location = useLocation();

  // Show loading state
  if (loading) {
    return <div>Loading...</div>;
  }

  // Check if user is authenticated
  if (!authenticated) {
    // Directly redirect to Keycloak authentication
    const currentPath = location.pathname + location.search;
    KeycloakService.login(currentPath);
    // Return a loading state while redirecting
    return <div>Redirecting to authentication...</div>;
  }

  // If no roles are required, allow access
  if (!roles.length) {
    return children;
  }

  // Convert roles to lowercase for case-insensitive comparison
  const normalizedRoles = roles.map(role => role.toLowerCase());

  // Check if user has required roles
  const hasRequiredRoles = requireAllRoles
    ? hasAllRoles(normalizedRoles)
    : hasAnyRole(normalizedRoles);

  // If user doesn't have required roles, redirect to unauthorized page
  if (!hasRequiredRoles) {
    console.log('User does not have required roles:', normalizedRoles);
    return <Navigate to="/unauthorized" replace />;
  }

  // User is authenticated and authorized, render the children
  return children;
};

export default ProtectedRoute;